import React from 'react';

import { useDropzone } from 'react-dropzone';
import { Center } from '@chakra-ui/react';

import InfoDragNDrop from '@/assets/InfoDragNDrop';
import UploadIcon from '@/assets/UploadIcon';

type FileUploaderProps = {
    onDrop: (acceptedFiles: File[]) => void;
    isInvalid: boolean;
    type?: string;
    isDisabled: boolean;
    isPadding?: boolean;
};

type AcceptedFileTypes = {
    [key: string]: string[];
};

const getDropzoneErrorMessage = (uploadType: string, errorCode: string, fallbackMessage: string): string => {
    if (uploadType === 'sire-review' && errorCode === 'file-invalid-type') {
        return 'File type must be PDF/XLS/XLSX';
    }

    return fallbackMessage;
};

const getAcceptedType = (type: string): AcceptedFileTypes => {
    if (type === 'invoice' || type === 'supporting-files') {
        return {
            'image/jpg': [],
            'image/jpeg': [],
            'image/png': [],
            'application/pdf': []
        };
    } else if (type.includes('registration')) {
        return {
            'image/jpg': [],
            'image/jpeg': [],
            'image/png': [],
            'application/pdf': [],
            'application/msword': [],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': []
        };
    } else if (type.includes('faktur')) {
        return {
            'application/pdf': [],
            'application/msword': [],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': []
        };
    } else if (type.includes('inspection-result') || type === 'sire-report' || type === 'manual') {
        return {
            'application/pdf': []
        };
    } else if (type.includes('sire-review')) {
        return {
            'application/pdf': ['.pdf'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        };
    } else if (type.includes('inspection-process')) {
        return {
            'image/jpg': [],
            'image/jpeg': [],
            'image/png': [],
            'application/pdf': [],
            'application/vnd.ms-excel': [],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': []
        };
    } else {
        return {
            'image/jpg': [],
            'image/jpeg': [],
            'image/png': []
        };
    }
};

const FileUploader: React.FC<FileUploaderProps> = ({ onDrop, isInvalid, type = 'invoice', isDisabled, isPadding }) => {
    const { acceptedFiles, fileRejections, getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: getAcceptedType(type),
        disabled: isDisabled
    });

    const acceptedFileItems = acceptedFiles.map((file: File) => (
        <p key={file.name}>
            {file.name} - {(file.size / (1024 * 1024)).toFixed(2)} MB
        </p>
    ));

    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        <li key={file.name}>
            {file.name} - {(file.size / (1024 * 1024)).toFixed(2)} MB
            <ul>
                {errors.map((e) => (
                    <li key={e.code} style={{ color: 'red' }}>{getDropzoneErrorMessage(type, e.code, e.message)}</li>
                ))}
            </ul>
        </li>
    ));

    return (
        <section style={{ padding: isPadding ? '30px !important' : '0px !important', marginLeft: '0px' }}>
            <div
                {...getRootProps({ className: 'dropzone' })}
                style={{
                    border: isInvalid ? '2px solid red' : '1px solid #EAECF0',
                    borderRadius: '13px',
                    padding: '20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? '#e0e0e0' : '#ffffff',
                }}
            >
                <input {...getInputProps()} />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <UploadIcon />
                </div>
                <Center>
                    <InfoDragNDrop />
                </Center>
                {type === 'invoice' && <p style={{ color: '#475467', fontSize: '13px' }}>JPG, JPEG, PNG or PDF (Max. 1 MB)</p>}
                {type === 'template' && <p style={{ color: '#475467', fontSize: '13px' }}>JPG, JPEG or PNG (Max. 1 MB)</p>}
                {type === 'registration-inspectingcompany' && <p style={{ color: '#475467', fontSize: '13px' }}>JPG, JPEG, PNG, PDF or DOC (Max. 5 MB)</p>}
                {type === 'registration-shipoperator' && <p style={{ color: '#475467', fontSize: '13px' }}>JPG, JPEG, PNG, PDF or DOC (Max. 10 MB)</p>}
                {type === 'faktur' && <p style={{ color: '#475467', fontSize: '13px' }}>PDF or DOC (Max. 5 MB)</p>}
                {type === 'inspection-result' && <p style={{ color: '#475467', fontSize: '13px' }}>PDF (Max. 5 MB)</p>}
                {type === 'supporting-files' && <p style={{ color: '#475467', fontSize: '13px' }}>JPG, JPEG, PNG or PDF (Max. 5 MB)</p>}
                {type === 'sire-review' && <p style={{ color: '#475467', fontSize: '13px' }}>PDF, XLS or XLSX (Max. 30 MB)</p>}
                {type === 'inspection-process' && <p style={{ color: '#475467', fontSize: '13px' }}>JPG, JPEG, PNG, PDF or Excel (Max. 30 MB)</p>}
                {type === 'sire-report' && <p style={{ color: '#475467', fontSize: '13px' }}>PDF (Max. 10 MB)</p>}
                {type === 'manual' && <p style={{ color: '#475467', fontSize: '13px' }}>PDF (Max. 20 MB)</p>}
                {acceptedFileItems.length > 0 && <div style={{ marginTop: '20px', color: 'grey' }}>{acceptedFileItems}</div>}
                {fileRejectionItems.length > 0 && <div>{fileRejectionItems}</div>}
            </div>
        </section>
    );
};

export default FileUploader;
